<script setup lang="ts">

import { ref, computed } from 'vue';
import { useViewModelInstance } from '@app/MyGarage';
import { useCarePlanApi } from '@app/MyGarage/Types/Api/CarePlanApi';

import LoadingPanel from '@app/Components/FlowPanels/LoadingPanel.vue';
import ShieldWatermark from './Components/ShieldWatermark.vue';
import CarbucksProtectFeatureIcons from "./Components/CarbucksProtectFeatureIcons.vue";
import JupiterButton from '@modules/Jupiter/Components/JupiterButton.vue';
import { Settings } from '@/globals';
import dayjs from 'dayjs';

const _carePlanApi = useCarePlanApi();

const model = await useViewModelInstance();
const documents = await _carePlanApi.getCarbucksProtectDocumentUrls( model.vehicle.vehicleId );
const termsAndConditionsLinkUrl = ref( documents?.termsAndConditionsLinkUrl );
const conciergePhoneNumber = Settings.Concierge.PhoneNumber;

const panels = {
    introduction: 'introduction',
    acknowledgement: 'acknowledgement',
    activated: 'activated',
    loading: 'loading',
    error: 'error'
}

const currentPanel = ref( panels.introduction );
const carbucksProtectActivated = ref( false );
const termsAndConditionsClicked = ref( false );

// this is the list of checkboxes items that appear inside the cards. 
// these are all required to be checked before the user can activate their cover
// to get a new item to render, add a new object to the array and the loop in the template will handle it automatically
const checkboxItems = ref([
    {
        id: 'terms',
        text: 'I have read and understood the terms and conditions',
        checked: false
    },
    {
        id: 'dealership',
        text: 'All repairs are to be completed by the selling dealership from which you bought the vehicle',
        checked: false
    },
    {
        id: 'workorder',
        text: 'I understand the maximum Work Order cover of each wear & tear item',
        checked: false
    },
    {
        id: 'app',
        text: 'I must have the Carbucks App installed to approve repairs made on my vehicle',
        checked: false
    }
]);

/**
 * Determines whether all the checkboxes are selected
 * used to control the disabled state of the activate button
 */
const allCheckboxesSelected = computed( () => checkboxItems.value.every( item => item.checked ) );
const protectCoverStarts = ref( model.carePlan.carbucksProtect.dateActiveFrom );
const showStatutoryWarning = computed( () => model.carePlan.carbucksProtect.isUsed && protectCoverStarts.value && dayjs( protectCoverStarts.value ).isAfter( dayjs() ) );

/**
 * Resets the flow to the introduction panel including all state
 */
const reset = () => {
    termsAndConditionsClicked.value = false;

    checkboxItems.value.forEach( item => item.checked = false );

    currentPanel.value = panels.introduction;
}

/**
 * Handles the introduction panel being completed
 */
const onIntroCompleted = () => currentPanel.value = panels.acknowledgement;

/**
 * Handles the terms and conditions being clicked
 */
const onTermsAndConditionsClicked = async () => 
{
    // simulate a delay to ensure the content transition happens behind the T&Cs webview
    await new Promise(resolve => setTimeout( resolve, 500 ));

    termsAndConditionsClicked.value = true;
}

/**
 * Handles the terms and conditions being acknowledged
 */
const onTermsAndConditionsAcknowledged = async () => {
    currentPanel.value = panels.loading;

    const acknowledgementResult = await _carePlanApi.acknowledgeCarbucksProtect( model.vehicle.vehicleId );
    carbucksProtectActivated.value = acknowledgementResult.success;

    if( !acknowledgementResult.success )
    {
        currentPanel.value = panels.error;
        return;
    }
    
    protectCoverStarts.value = dayjs( acknowledgementResult.data.dateActiveFrom );
    currentPanel.value = panels.activated;
}

</script>

<template>
    <div class="page-content overflow-hidden">
        <div class="flex flex-col items-center text-lg h-full tracking-wider">

            <TabPanels v-model="currentPanel" 
                       class="w-full !h-full px-4 flex-grow overflow-y-auto"
                       :touch="{ left: null, right: null }">
            
                <!-- PROTECT INTRODUCTION PANEL -->
                <TabPanel :value="panels.introduction"
                          class="flex flex-col h-full bg-gray-900">

                    <div class="flex flex-col px-4 items-center my-4">
                        <!-- logo -->
                        <img src="@/assets/images/protect-logo.png" class="w-3/5 mb-6" />

                        <!-- subtitle -->
                        <h2 class="text-base text-white tracking-wider text-center min-h-[4.5rem]">
                            Congratulations on purchasing <span class="font-bold">{{ model.carePlan.carbucksProtect.title }}</span> covering your vehicle's wear and tear items
                        </h2>
                    </div>

                    <div class="flex-1 h-full relative flex flex-col justify-center items-center pb-16">
                        <!-- feature icons overlayed on the shield watermark -->
                        <div class="flex flex-col justify-center items-center flex-1">
                            <div class="relative flex-1 flex items-center justify-center">
                                <ShieldWatermark />
                                <div class="absolute flex flex-wrap justify-center
                                            uppercase font-bold text-sm lg:text-base 3xl:text-xl">

                                    <CarbucksProtectFeatureIcons :model="model.carePlan.carbucksProtect" />
                                </div>
                            </div>
                        </div>

                        <!-- continue button -->
                        <div class="w-full">
                            <Button
                                variant="tonal"
                                @click="onIntroCompleted"
                                class="!h-11 lg:!h-12 w-full mb-4">
                                Continue
                            </Button>
                        </div>
                    </div>
                </TabPanel>

                <!-- VIEW T&CS AND ACKNOWLEDGEMENT PANEL -->
                <TabPanel :value="panels.acknowledgement"
                          class="flex flex-col h-full">

                    <div class="flex-1 overflow-y-auto no-scrollbar">
                        <div class="flex flex-col px-4 items-center">

                            <!-- logo -->
                            <div class="relative w-full flex justify-center my-4">
                                <img src="@/assets/images/protect-logo.png" class="w-3/5" />
                            </div>
    
                            <!-- subtitle -->
                            <h2 class="text-base text-white tracking-wider text-center min-h-[4.5rem]">
                                To activate your <span class="font-bold">{{ model.carePlan.carbucksProtect.title }}</span>
                                <br /> 
                                please agree to the below
                            </h2>
    
                            <!-- please read t&cs card -->
                            <div class="h-24 mb-4 mt-6">
                                <JupiterButton 
                                    v-if="termsAndConditionsLinkUrl"  
                                    variant="text"
                                    color="secondary"
                                    type="url" 
                                    :to="termsAndConditionsLinkUrl"
                                    @click="onTermsAndConditionsClicked"
                                    class="h-auto !p-0 !w-full">
                                        <v-card class="bg-gray-700 text-white h-24 rounded-lg justify-center"
                                            :class="{ 'border-secondary-500 ring-2 ring-secondary-500 bg-secondary-800/50': termsAndConditionsClicked }">
                                            <v-card-text class="flex items-center justify-between p-4 w-full h-full">
                                                <div class="flex-1 pr-4">
                                                    <p class="text-sm text-wrap leading-tight">Before we go any further, please take a moment to read our Terms and Conditions</p>
                                                </div>
                                                <Icon name="link" class="text-2xl ml-2" />
                                            </v-card-text>
                                        </v-card>
                                </JupiterButton>
                            </div>
                                
                            <!-- cards with checkboxes (to add one, add an object to the checkboxItems array) -->
                            <div class="w-full space-y-3 mb-6">
                                <div 
                                    v-for="item in checkboxItems" 
                                    :key="item.id"
                                    @click="termsAndConditionsClicked ? ( item.checked = !item.checked ) : null"
                                    class="w-full bg-gray-700 h-24 p-4 rounded-lg transition-all duration-200 relative flex justify-center items-center border-2"
                                    :class="{
                                        'border-secondary-500 bg-secondary-800/50': item.checked && termsAndConditionsClicked,
                                        'border-gray-700': !item.checked,
                                        'bg-gray-900/60 border-gray-900/60': !termsAndConditionsClicked,
                                        'cursor-pointer': termsAndConditionsClicked,
                                        'cursor-not-allowed': !termsAndConditionsClicked
                                    }">
                                    
                                        <!-- overlay for the disabled state -->
                                        <div 
                                            v-if="!termsAndConditionsClicked"
                                            class="absolute inset-0 bg-gray-900/60 rounded-lg z-10">
                                        </div>
                                        
                                        <!-- checkbox and text -->
                                        <div class="flex items-center gap-3">
                                            <CheckBox v-model="item.checked"
                                                color="secondary"
                                                class="flex-none" />

                                            <p class="text-sm leading-tight flex items-center"
                                            :class="{
                                                'text-white': termsAndConditionsClicked,
                                                'text-gray-500': !termsAndConditionsClicked
                                            }">
                                                {{ item.text }}
                                            </p>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- activate my cover button -->
                    <div class="w-full px-4 pt-2">
                        <Button
                            color="secondary"
                            :disabled="!allCheckboxesSelected"
                            @click="onTermsAndConditionsAcknowledged"
                            class="!h-11 lg:!h-12 w-full mb-6"
                            :class="{
                                'opacity-50 cursor-not-allowed': !allCheckboxesSelected
                            }">
                            Activate My Cover
                        </Button>
                    </div>

                </TabPanel>

                <!-- ACKNOWLEDGEMENT SUCCESS PANEL -->
                <TabPanel :value="panels.activated" class="flex flex-col h-full">

                    <div class="flex-1 flex flex-col">
                        <!-- LOGO & BACK BUTTON -->
                        <div class="px-4 pt-4 my-4">
                            <div class="relative w-full flex justify-center">
                                    <img src="@/assets/images/protect-logo.png" class="w-3/5" />
                            </div>
                        </div>

                        <div class="flex-1 flex flex-col px-4 text-center">  
                                <h1 class="text-m text-white tracking-wider uppercase font-bold">
                                    Congratulations
                                </h1>
                                <h2 class="text-sm text-white tracking-wider uppercase opacity-80 mt-2">
                                    Your cover has been activated
                                </h2>
                                <p class="text-sm text-white/60 tracking-wider mt-4">
                                    We've just sent you an email containing a copy of the agreement and the terms &amp; conditions of the cover.
                                </p>
                                <p class="text-sm text-white/60 tracking-wider mt-2">
                                    Remember that you can always access your documents from My Garage inside the app, so you never have to lose your documents again
                                </p>
                                <p v-if="showStatutoryWarning" class="text-sm text-warning tracking-wider mt-2">
                                    Looks like your car is covered as part of the dealership's statutory warranty. To ensure none of your PROTECT work orders are used unintentionally, your contract start date will be 90 days from today.
                                </p>
                        </div>
                    </div>

                    <!-- Continue Button -->
                    <div class="w-full px-4 pb-4">
                        <Button
                            variant="tonal"
                            class="!h-11 lg:!h-12 w-full mb-6"
                            :to="'/my-garage'">
                            Continue
                        </Button>
                    </div>
                </TabPanel>

                <!-- ACKNOWLEDGEMENT ERROR PANEL -->
                <TabPanel :value="panels.error" class="flex flex-col h-full p-4">
                    
                   <div class="flex-1 flex flex-col">
                       <!-- LOGO -->
                       <div class="shrink-0 text-center transition-all duration-300 ease-in
                                   pt-6 opacity-100 overflow-hidden -translate-y-2 mt-4">
                           <div class="flex flex-col items-center">
                               <img src="@/assets/images/protect-logo.png" class="h-6 mb-4" />
       
                               <div class="flex flex-wrap justify-center text-center font-bold text-sm">
                                    <div class="text-2xl uppercase text-primary-500">SORRY!</div>
                                    <div class="text-sm mb-2 text-primary-500">
                                        Looks like we have encountered an unexpected error when activating your cover!
                                    </div>
                                    <p class="text-sm">
                                        So we can assist in resolving this, please contact your Concierge
                                    </p>
                                </div>
                           </div>
                       </div>
                   </div>
                    
                    <div class="w-full">
                        <JupiterButton
                            variant="outlined"
                            size="large"
                            type="tel"
                            :to="conciergePhoneNumber"
                            class="w-full mt-3 items-center justify-center lg:mt-4 3xl:mt-6 3xl:text-xl mb-2">
                                <span class="flex items-center gap-1">
                                    Contact Concierge
                                    <Icon name="phone" class="text-2xl ml-2" />
                                </span>
                        </JupiterButton> 

                        <Button variant="tonal"
                                size="large"
                                class="w-full mb-6"
                                @click="reset">
                            Try again
                        </Button>
                   </div>
                </TabPanel>

                <!-- LOADING PANEL -->
                <TabPanel :value="panels.loading" class="flex flex-col h-full">
                    <LoadingPanel :loadingMessages="['Activating your contract']" />
                </TabPanel>

            </TabPanels>
        </div>
    </div>
</template>

<style scoped>

.no-scrollbar::-webkit-scrollbar {
    display: none;
}
.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

</style>